import { db } from "./db";
import type { AppSettings, CompleteBackup } from "./types";

export const DEFAULT_BACKUP_REMINDER_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

export function isBackupReminderDue(settings: AppSettings, now = Date.now()) {
  if (settings.sessionsSinceLastBackup >= settings.backupReminderSessions) return true;
  const reminderStartedAt = settings.lastBackupAt ?? settings.backupReminderStartedAt;
  if (reminderStartedAt == null) return false;
  const reminderDays = Math.max(1, settings.backupReminderDays ?? DEFAULT_BACKUP_REMINDER_DAYS);
  return now - reminderStartedAt >= reminderDays * DAY_MS;
}

export async function exportCompleteBackup() {
  const exportedAt = Date.now();
  const updates = {
    backupReminderDays: DEFAULT_BACKUP_REMINDER_DAYS,
    backupReminderStartedAt: exportedAt,
    lastBackupAt: exportedAt,
    sessionsSinceLastBackup: 0,
  };
  const backup = await createBackup();
  backup.exportedAt = exportedAt;
  backup.data.settings = backup.data.settings.map((item) => ({ ...item, ...updates }));
  downloadJson(backup, `forge-backup-${new Date(exportedAt).toISOString().slice(0, 10)}.json`);
  await db.settings.update("settings", updates);
  return exportedAt;
}

export async function createBackup(): Promise<CompleteBackup> {
  return db.transaction("r", [db.exercises, db.templates, db.programs, db.sessions, db.settings, db.imports], async () => {
    const [exercises, templates, programs, sessions, settings, imports] = await Promise.all([
      db.exercises.toArray(),
      db.templates.toArray(),
      db.programs.toArray(),
      db.sessions.toArray(),
      db.settings.toArray(),
      db.imports.toArray(),
    ]);
    return {
      format: "forge-workout-backup",
      backupSchemaVersion: 1,
      exportedAt: Date.now(),
      data: { exercises, templates, programs, sessions, settings, imports },
    };
  });
}

export function validateBackup(value: unknown): asserts value is CompleteBackup {
  if (!value || typeof value !== "object") throw new Error("Le fichier n’est pas un objet JSON valide.");
  const backup = value as Partial<CompleteBackup>;
  if (backup.format !== "forge-workout-backup") throw new Error("Ce fichier n’est pas une sauvegarde Forge.");
  if (backup.backupSchemaVersion !== 1) throw new Error("Version de sauvegarde non prise en charge.");
  if (!backup.data || typeof backup.data !== "object") {
    throw new Error("Sauvegarde incomplète : données obligatoires absentes.");
  }
  const invalid = () => { throw new Error("Sauvegarde incomplète ou corrompue. Les données actuelles sont conservées."); };
  const object = (item: unknown): item is Record<string, unknown> => Boolean(item && typeof item === "object" && !Array.isArray(item));
  const text = (item: unknown): item is string => typeof item === "string" && item.trim().length > 0;
  const number = (item: unknown): item is number => typeof item === "number" && Number.isFinite(item) && item >= 0;
  const tables = ["exercises", "templates", "programs", "sessions", "settings", "imports"] as const;
  for (const table of tables) {
    const rows = backup.data[table];
    if (!Array.isArray(rows)) invalid();
    const ids = new Set();
    for (const row of rows) {
      if (!object(row) || !text(row.id) || ids.has(row.id)) invalid();
      ids.add(row.id);
    }
  }
  if (backup.data.settings.length !== 1) invalid();
  const settings = backup.data.settings[0];
  if (settings.id !== "settings" || typeof settings.darkMode !== "boolean" || typeof settings.soundEnabled !== "boolean" || !number(settings.volume) || settings.volume > 1 || !text(settings.defaultUnit) || !number(settings.sessionsSinceLastBackup) || !number(settings.backupReminderSessions) || settings.backupReminderSessions < 1) invalid();
  for (const item of backup.data.exercises) {
    if (!text(item.name) || !["reps", "duration"].includes(item.trackingMode) || !["double_progression", "manual", "duration", "discrete", "none"].includes(item.progressionMode) || typeof item.archived !== "boolean") invalid();
  }
  const exerciseIds = new Set(backup.data.exercises.map((item) => item.id));
  const templateIds = new Set(backup.data.templates.map((item) => item.id));
  for (const item of backup.data.programs) {
    if (!text(item.name) || !Array.isArray(item.templateIds) || item.templateIds.some((id) => !templateIds.has(id))) invalid();
  }
  for (const item of backup.data.templates) {
    if (!text(item.name) || !Array.isArray(item.exercises)) invalid();
    for (const entry of item.exercises) {
      if (!object(entry) || !text(entry.id) || !exerciseIds.has(entry.exerciseDefinitionId) || !Number.isInteger(entry.setCount) || entry.setCount < 1 || entry.setCount > 20 || !number(entry.load) || !number(entry.restSeconds) || !text(entry.unit)) invalid();
      const range = entry.minReps != null ? [entry.minReps, entry.maxReps] : [entry.minDuration, entry.maxDuration];
      if (!number(range[0]) || !number(range[1]) || range[1]! < range[0]!) invalid();
    }
  }
  const validSets = (sets: unknown) => Array.isArray(sets) && sets.every((set: unknown) => object(set) && text(set.id) && number(set.load) && text(set.unit) && number(set.setNumber) && (set.reps == null || number(set.reps)) && (set.duration == null || number(set.duration)) && (set.rir == null || number(set.rir)) && (set.completedAt == null || number(set.completedAt)));
  for (const item of backup.data.sessions) {
    if (!text(item.templateNameSnapshot) || !number(item.startedAt) || !["active", "completed", "abandoned"].includes(item.status) || !Number.isInteger(item.currentExerciseIndex) || item.currentExerciseIndex < 0 || !Array.isArray(item.performances)) invalid();
    if (item.restTimer && (!number(item.restTimer.restEndAt) || !number(item.restTimer.restStartedAt) || !number(item.restTimer.durationSeconds))) invalid();
    for (const entry of item.performances) {
      if (!object(entry) || !text(entry.exerciseDefinitionId) || !text(entry.exerciseNameSnapshot) || !object(entry.prescriptionSnapshot) || !["reps", "duration"].includes(entry.prescriptionSnapshot.trackingMode) || !number(entry.prescriptionSnapshot.setCount) || !validSets(entry.sets)) invalid();
    }
  }
}

export async function restoreBackup(backup: CompleteBackup) {
  validateBackup(backup);
  const restoredAt = Date.now();
  const settings = backup.data.settings.map((item) => ({
    vibrationEnabled: true,
    backupReminderDays: DEFAULT_BACKUP_REMINDER_DAYS,
    backupReminderStartedAt: item.lastBackupAt ?? restoredAt,
    ...item,
    onboardingComplete: item.onboardingComplete ?? backup.data.programs.length > 0,
  }));
  const programs = backup.data.programs.map((item) => ({ ...item, archived: item.archived ?? false, isCurrent: item.isCurrent ?? item.active ?? false }));
  if (!programs.some((item) => item.isCurrent && !item.archived)) {
    const first = programs.find((item) => !item.archived);
    if (first) first.isCurrent = true;
  }
  await db.transaction(
    "rw",
    [db.exercises, db.templates, db.programs, db.sessions, db.settings, db.imports],
    async () => {
      await Promise.all([
        db.exercises.clear(),
        db.templates.clear(),
        db.programs.clear(),
        db.sessions.clear(),
        db.settings.clear(),
        db.imports.clear(),
      ]);
      await db.exercises.bulkAdd(backup.data.exercises);
      await db.templates.bulkAdd(backup.data.templates);
      await db.programs.bulkAdd(programs);
      await db.sessions.bulkAdd(backup.data.sessions);
      await db.settings.bulkAdd(settings);
      await db.imports.bulkAdd(backup.data.imports);
    },
  );
}

export function downloadJson(value: unknown, filename: string) {
  const blob = new Blob([JSON.stringify(value, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.hidden = true;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
